import { FormEvent, useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import "../../main.scss"
import { resolveApiBaseUrl } from "../api/apiBase"
import "./User.scss"
import { authAPI, isDemoAccountEmail } from "../api/authApi"
import { formatAuthRateLimitMessage } from "../api/apiError"
import Logo from "@/components/Logo"
import { Badge } from "@/components/ui/badge"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

type TenantSummary = {
  uid: string;
  name: string;
  abbreviation: string;
  description?: string;
  imageUrl?: string;
};

function Registration() {
  const location = useLocation()
  const { tenantAbbreviation: tenantAbbreviationFromParams } = useParams<{ tenantAbbreviation?: string }>()
  const [searchParams] = useSearchParams()
  const stateEmail = (location.state as { email?: string } | null)?.email ?? ""
  const returnPath = searchParams.get("returnPath") ?? undefined
  const [email, setEmail] = useState(stateEmail)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [formLocked, setFormLocked] = useState(false)
  const [rateLimitUntil, setRateLimitUntil] = useState<number | null>(null)
  const [remainingSeconds, setRemainingSeconds] = useState(0)
  const [showWaitMessage, setShowWaitMessage] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [tenantPopoverOpen, setTenantPopoverOpen] = useState(false)
  const [tenantSearch, setTenantSearch] = useState("")
  const [tenantLoading, setTenantLoading] = useState(false)
  const [tenantError, setTenantError] = useState<string | null>(null)
  const [tenants, setTenants] = useState<TenantSummary[]>([])
  const [selectedTenant, setSelectedTenant] = useState<TenantSummary | null>(null)

  const handleRegister = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (rateLimitUntil != null && Date.now() < rateLimitUntil) {
      setShowWaitMessage(true)
      return
    }
    if (busy || formLocked) {
      return
    }

    setBusy(true)
    setError("")
    setSuccess("")
    setShowWaitMessage(false)
    try {
      const normalizedEmail = email.trim().toLowerCase()
      if (!normalizedEmail) {
        setError("Email is required")
        return
      }

      if (isDemoAccountEmail(normalizedEmail)) {
        setError("This email uses demo login. Sign in with your password instead.")
        return
      }

      const result = await authAPI.registerInitiate(normalizedEmail, {
        returnPath: returnPath && returnPath.startsWith("/") ? returnPath : undefined,
        tenantAbbreviation: selectedTenant?.abbreviation ?? tenantAbbreviationFromParams ?? undefined,
      })
      setEmail(normalizedEmail)
      setSuccess(result.message || "Check your email for a registration link")
      setFormLocked(true)
    } catch (err: any) {
      setSuccess("")
      if (err?.status === 429) {
        const secs = err?.retryAfterSeconds ?? 60
        setRateLimitUntil(Date.now() + secs * 1000)
        setError(formatAuthRateLimitMessage(secs))
      } else {
        setError(err.message || "Registration failed")
      }
    } finally {
      setBusy(false)
    }
  }

  const navLogin = () => {
    const path = (selectedTenant?.abbreviation || tenantAbbreviationFromParams)
      ? `/login/${encodeURIComponent(selectedTenant?.abbreviation || tenantAbbreviationFromParams || "")}`
      : "/login"
    navigate(path, { state: { email: email.trim() || undefined } })
  }

  const keyPress = (event: any) => {
    if (event.key === "Enter" && !busy) {
      event.preventDefault()
      const form = event.target.closest("form")
      if (form) {
        form.requestSubmit()
      }
    }
  }

  useEffect(() => {
    if (error && containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }, [error])

  // If /register/:tenantAbbreviation is used, resolve that organization
  useEffect(() => {
    const abbr = tenantAbbreviationFromParams?.trim() || ""
    if (!abbr) {
      return
    }
    let cancelled = false
    const fetchTenant = async () => {
      try {
        setTenantLoading(true)
        setTenantError(null)
        const baseUrl = resolveApiBaseUrl()
        const response = await fetch(`${baseUrl}/tenants/by-abbreviation/${encodeURIComponent(abbr)}`)
        if (!response.ok) {
          setSelectedTenant(null)
          return
        }
        const data = await response.json().catch(() => null)
        if (!data?.success || !data.tenant || cancelled) {
          return
        }
        const t = data.tenant as TenantSummary
        setSelectedTenant(t)
      } catch (err) {
        setTenantError((err as Error).message || "Unable to load organization")
      } finally {
        if (!cancelled) {
          setTenantLoading(false)
        }
      }
    }
    fetchTenant()
    return () => {
      cancelled = true
    }
  }, [tenantAbbreviationFromParams])

  // Restore last used organization if no URL param
  useEffect(() => {
    if (tenantAbbreviationFromParams) {
      return
    }
    const stored = window.localStorage.getItem("labwick_last_tenant")
    if (!stored) {
      return
    }
    try {
      const parsed = JSON.parse(stored) as TenantSummary
      if (parsed && parsed.uid && parsed.name && parsed.abbreviation) {
        setSelectedTenant(parsed)
      }
    } catch {
      // ignore
    }
  }, [tenantAbbreviationFromParams])

  // Persist selected organization
  useEffect(() => {
    if (selectedTenant) {
      window.localStorage.setItem("labwick_last_tenant", JSON.stringify(selectedTenant))
    } else {
      window.localStorage.removeItem("labwick_last_tenant")
    }
  }, [selectedTenant])

  useEffect(() => {
    if (rateLimitUntil == null) return
    const tick = () => {
      const secs = Math.max(0, Math.ceil((rateLimitUntil - Date.now()) / 1000))
      setRemainingSeconds(secs)
      if (secs <= 0) {
        setRateLimitUntil(null)
        setShowWaitMessage(false)
      }
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [rateLimitUntil])

  return (
    <div className="auth-page" ref={containerRef}>
      <div className="lw-home">
        <header className="lw-nav">
          <Logo size="md" withText />
        </header>

        <main className="lw-hero">
          <div className="lw-hero-copy">
            <span className="lw-hero-eyebrow">Labwick.com</span>
            <h1 className="lw-hero-title text-blue-400">Tailor-made collection management for your team.</h1>
            <p className="lw-hero-subtitle">
              Labwick helps operations and research teams design flexible collections, capture richer data,
              and connect public forms directly to the workflows that actually run the business.
            </p>
          </div>

          <div className="lw-hero-card">
            <form className="auth-card panel" onSubmit={handleRegister}>
              <div className="auth-header">
                <h1>Request an account</h1>
                <p className="auth-subtitle">
                  Tell us your email and we will send a registration link.
                </p>
              </div>
              {error && <p className="auth-alert auth-alert--error">{error}</p>}
              {success && <p className="auth-alert auth-alert--success">{success}</p>}
              <label className="auth-label" htmlFor="register-email">
                Email address
              </label>
              <input
                id="register-email"
                type="email"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="textInput auth-input"
                onKeyUp={keyPress}
                required
                disabled={busy || formLocked}
                autoComplete="email"
              />
              <div className="mt-3">
                <Popover open={tenantPopoverOpen} onOpenChange={setTenantPopoverOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-full flex items-center justify-between border rounded px-3 py-2 text-sm bg-white hover:bg-gray-50"
                      style={{ padding: "6px 6px 6px 12px" }}
                    >
                      <span className="flex items-center gap-2 truncate">
                        {selectedTenant?.imageUrl && (
                          <img
                            src={selectedTenant.imageUrl}
                            alt=""
                            className="h-5 w-5 rounded object-contain flex-shrink-0"
                          />
                        )}
                        {selectedTenant ? (
                          <>
                            <span className="font-medium truncate">
                              {selectedTenant.name}
                            </span>
                            <Badge variant="outline" className="ml-1 flex-shrink-0">
                              {selectedTenant.abbreviation}
                            </Badge>
                          </>
                        ) : (
                          <span className="text-gray-500 truncate" style={{ fontSize: 11 }}>
                            Select organisation (optional)
                          </span>
                        )}
                      </span>
                      {selectedTenant && (
                        <button
                          type="button"
                          className="ml-2 text-xs text-gray-500 hover:text-gray-700"
                          onClick={(event) => {
                            event.stopPropagation()
                            setSelectedTenant(null)
                          }}
                        >
                          Clear
                        </button>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-[320px]">
                    <Command>
                      <CommandInput
                        placeholder="Search organizations..."
                        value={tenantSearch}
                        onValueChange={(value) => {
                          setTenantSearch(value)
                          const run = async () => {
                            const trimmed = value.trim()
                            if (!trimmed || trimmed.length < 3) {
                              setTenants([])
                              setTenantError(null)
                              setTenantLoading(false)
                              return
                            }
                            try {
                              setTenantLoading(true)
                              setTenantError(null)
                              const baseUrl = resolveApiBaseUrl()
                              const url = new URL(`${baseUrl}/tenants`)
                              if (value && value.trim()) {
                                url.searchParams.set("search", value.trim())
                              }
                              const response = await fetch(url.toString())
                              if (!response.ok) {
                                setTenants([])
                                return
                              }
                              const data = await response.json().catch(() => null)
                              if (!data?.success || !Array.isArray(data.tenants)) {
                                setTenants([])
                                return
                              }
                              setTenants(data.tenants as TenantSummary[])
                            } catch (err) {
                              setTenantError((err as Error).message || "Unable to load organizations")
                            } finally {
                              setTenantLoading(false)
                            }
                          }
                          run()
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && tenants.length === 1) {
                            event.preventDefault()
                            const only = tenants[0]
                            setSelectedTenant(only)
                            setTenantPopoverOpen(false)
                          }
                        }}
                      />
                      <CommandEmpty>
                        {tenantLoading ? "Loading organizations..." : "Start typing to find your organization"}
                      </CommandEmpty>
                      <CommandGroup>
                        {tenants.map((tenant) => (
                          <CommandItem
                            key={tenant.uid}
                            value={`${tenant.name} ${tenant.abbreviation}`}
                            onSelect={() => {
                              setSelectedTenant(tenant)
                              setTenantPopoverOpen(false)
                            }}
                          >
                            <div className="flex items-center gap-2">
                              {tenant.imageUrl && (
                                <img
                                  src={tenant.imageUrl}
                                  alt=""
                                  className="h-5 w-5 rounded object-contain flex-shrink-0"
                                />
                              )}
                              <span className="font-medium">{tenant.name}</span>
                              <Badge variant="outline" className="ml-1">
                                {tenant.abbreviation}
                              </Badge>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </Command>
                    {tenantError && (
                      <div className="px-3 py-2 text-xs text-red-600 border-t border-gray-100">
                        {tenantError}
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
              <button
                type="submit"
                className={
                  "buttonSubmit auth-button" +
                  (busy ? " opacity-50 cursor-progress" : "") +
                  (remainingSeconds > 0 ? " opacity-70 cursor-not-allowed" : "")
                }
                disabled={busy || formLocked}
              >
                {formLocked
                  ? "Link Sent"
                  : remainingSeconds > 0
                    ? "Wait"
                    : "Request access"}
              </button>
              {showWaitMessage && remainingSeconds > 0 && (
                <p className="auth-alert auth-alert--error">
                  Please wait {remainingSeconds} seconds before trying again.
                </p>
              )}
              {formLocked && (
                <p className="auth-help">
                  We disabled this form to prevent duplicate submissions. Use the link in your inbox to continue registration.
                </p>
              )}
              <button
                type="button"
                className="auth-footer auth-footer--boxed auth-footer--stack auth-footer--cta"
                onClick={navLogin}
              >
                <span>Already have a login link?</span>
                <span className="auth-footer__link">Log in</span>
              </button>
            </form>
          </div>
        </main>
      </div>
    </div>
  )
}
export default Registration
